import React, { useState, useEffect } from 'react';
import { ArrowLeft, RotateCcw, Trophy, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { vocabularyAPI, progressAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const MemoryMatch = () => {
  const [algerianCards, setAlgerianCards] = useState([]);
  const [englishCards, setEnglishCards] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [gameComplete, setGameComplete] = useState(false);
  const [timer, setTimer] = useState(0);
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gameStarted, setGameStarted] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadVocabulary();
  }, []);

  const loadVocabulary = async () => {
    try {
      setLoading(true);
      const response = await vocabularyAPI.getAll();
      const vocabularyWords = response.words || [];
      setWords(vocabularyWords);
      generateCards(vocabularyWords);
    } catch (error) {
      toast({
        title: "Error Loading Vocabulary",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate mixed cards for vocabulary
  const generateCards = (vocabularyWords = words) => {
    if (vocabularyWords.length === 0) return;
    
    // Shuffle all words first, then take 6 for better variety
    const shuffledWords = [...vocabularyWords].sort(() => Math.random() - 0.5);
    const selectedWords = shuffledWords.slice(0, 6);
    
    // Create all cards in one array
    const allCards = [];
    
    selectedWords.forEach((word, index) => {
      // Add Algerian word card
      allCards.push({
        id: `algerian-${index}`,
        content: word.algerianWord,
        arabicScript: word.arabicScript,
        pronunciation: word.pronunciation,
        type: 'algerian',
        pairId: word.id,
        word: word
      });
      
      // Add English translation card
      allCards.push({
        id: `english-${index}`,
        content: word.englishTranslation,
        type: 'english',
        pairId: word.id,
        word: word
      });
    });
    
    // Shuffle all cards together randomly
    const shuffledCards = allCards.sort(() => Math.random() - 0.5);
    setAlgerianCards(shuffledCards); // Reusing this state for all cards
    setEnglishCards([]); // No longer needed
    setMatchedPairs([]);
    setSelectedCards([]);
    setGameComplete(false);
    setTimer(0);
    setGameStarted(false);
  };

  // Timer effect
  useEffect(() => {
    if (gameStarted && !gameComplete && algerianCards.length > 0) {
      const interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [gameStarted, gameComplete, algerianCards.length]);

  // Check for matches when two cards are selected
  useEffect(() => {
    if (selectedCards.length === 2) {
      const [first, second] = selectedCards;
      
      if (first.pairId === second.pairId && first.type !== second.type) {
        // Match found!
        setMatchedPairs(prev => [...prev, first.pairId]);
        setSelectedCards([]);
        
        const algerianWord = first.type === 'algerian' ? first.content : second.content;
        const englishWord = first.type === 'english' ? first.content : second.content;
        
        toast({
          title: "Perfect Match! 🎉",
          description: `${algerianWord} = ${englishWord}`,
        });
        
        // Update progress for matched word
        const matchedWord = words.find(w => w.algerianWord === algerianWord);
        if (matchedWord) {
          progressAPI.update({
            category: matchedWord.category,
            wordId: matchedWord.id,
            gameType: "memory-match",
            score: 1,
            completed: true
          }).catch(error => console.error('Failed to update progress:', error));
        }
      } else {
        // No match - clear selection after brief delay
        setTimeout(() => {
          setSelectedCards([]);
        }, 800);
      }
    }
  }, [selectedCards, toast]);

  // Check if game is complete
  useEffect(() => {
    if (matchedPairs.length === algerianCards.length / 2 && algerianCards.length > 0) {
      setGameComplete(true);
      const minutes = Math.floor(timer / 60);
      const seconds = timer % 60;
      toast({
        title: "🏆 Congratulations!",
        description: `All pairs matched in ${formatTime(timer)}! ${timer < 60 ? 'Lightning fast!' : timer < 120 ? 'Great speed!' : 'Well done!'}`,
      });
    }
  }, [matchedPairs, algerianCards.length, timer, toast]);

  const handleCardClick = (card) => {
    // Start timer on first click
    if (!gameStarted) {
      setGameStarted(true);
    }
    
    // Ignore clicks if game is complete, card is already matched, or already selected
    if (
      gameComplete ||
      matchedPairs.includes(card.pairId) ||
      selectedCards.find(c => c.id === card.id) ||
      selectedCards.length >= 2
    ) {
      return;
    }
    
    setSelectedCards(prev => [...prev, card]);
  };

  const isCardMatched = (card) => {
    return matchedPairs.includes(card.pairId);
  };

  const isCardSelected = (card) => {
    return selectedCards.find(c => c.id === card.id);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const resetGame = () => {
    generateCards(words);
  };

  const getCardClass = (card) => {
    let baseClass = "cursor-pointer transition-all duration-200 transform hover:scale-105 border-2 ";
    
    if (isCardMatched(card)) {
      baseClass += "bg-green-100 border-green-400 text-green-800 opacity-75 cursor-not-allowed";
    } else if (isCardSelected(card)) {
      baseClass += card.type === 'algerian' 
        ? "bg-blue-200 border-blue-500 text-blue-900 shadow-lg scale-105"
        : "bg-purple-200 border-purple-500 text-purple-900 shadow-lg scale-105";
    } else {
      baseClass += card.type === 'algerian' 
        ? "bg-blue-50 border-blue-300 text-blue-800 hover:bg-blue-100"
        : "bg-purple-50 border-purple-300 text-purple-800 hover:bg-purple-100";
    }
    
    return baseClass;
  };

  const progress = (matchedPairs.length / (algerianCards.length / 2)) * 100;

  if (gameComplete) {
    const rating = timer <= 60 ? "⚡ Lightning Fast!" : timer <= 120 ? "🚀 Excellent Speed!" : timer <= 180 ? "👏 Great Job!" : "💪 Well Done!";
    
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <Card className="max-w-md mx-auto shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center justify-center space-x-2">
                <Trophy className="w-6 h-6 text-yellow-600" />
                <span>Speed Match Complete!</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-lg font-semibold text-gray-700">{rating}</div>
                
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">{formatTime(timer)}</div>
                  <div className="text-sm text-gray-600">Total Time</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {algerianCards.length / 2} pairs matched
                  </div>
                </div>
                
                <div className="flex space-x-4">
                  <Button onClick={resetGame} className="flex-1">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Play Again
                  </Button>
                  <Link to="/games" className="flex-1">
                    <Button variant="outline" className="w-full">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Games
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Link to="/games">
          <Button variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Games
          </Button>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Speed Match</h1>
        <Button variant="outline" size="sm" onClick={resetGame}>
          <RotateCcw className="w-4 h-4 mr-2" />
          New Game
        </Button>
      </div>

      {/* Stats */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">
            Matched: {matchedPairs.length}/{algerianCards.length / 2}
          </span>
          <div className="flex items-center space-x-1 text-sm text-gray-600">
            <Clock className="w-4 h-4" />
            <span className="font-mono text-lg">{formatTime(timer)}</span>
          </div>
        </div>
        <Progress value={progress} className="h-3" />
      </div>

      {/* Instructions */}
      <Card className="mb-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="p-4">
          <p className="text-gray-800 text-sm">
            <strong>🎯 Speed Match:</strong> All cards are visible! Click on an Algerian word, then click its English translation as fast as you can. 
            {!gameStarted && " Click any card to start the timer!"}
          </p>
        </CardContent>
      </Card>

      {/* Game Board - Mixed Grid */}
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {algerianCards.map((card) => (
          <Card
            key={card.id}
            className={getCardClass(card)}
            onClick={() => handleCardClick(card)}
          >
            <CardContent className="p-2 h-20 flex items-center justify-center">
              <div className="text-center w-full">
                <div className="font-semibold text-xs mb-1 leading-tight">
                  {card.content}
                </div>
                {card.arabicScript && card.type === 'algerian' && (
                  <div className="text-xs text-blue-600 mb-1 leading-tight" dir="rtl">
                    {card.arabicScript}
                  </div>
                )}
                {card.pronunciation && card.type === 'algerian' && (
                  <div className="text-xs opacity-75 italic leading-tight">
                    {card.pronunciation}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tips */}
      <div className="mt-8 text-center text-gray-500 text-sm">
        <p>💡 Tip: Try to remember where you saw each word to make matches faster!</p>
      </div>
    </div>
  );
};

export default MemoryMatch;